import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckoutContext } from '@/context/CheckoutContext';
import type { PaymentMethodEnum } from '@/types/common';
import type { ChargebeePaymentComponentContext, ChargebeePaymentIntent } from '@/types/chargebee';
import { CHARGEBEE_PAYMENT_BUTTON_ID, CHARGEBEE_PAYMENT_COMPONENT_ID } from '@/constants/chargebee';
import { chargebeeService, type ChargebeeMountedPaymentComponent } from '@/services/chargebeeService';
import { isChargebeeUserClosedError } from '@/utils/chargebeeWallets';
import { readChargebeeErrorText } from '@/utils/chargebeeErrorText';
import {
  isChargebeePaymentIntentAuthorized,
  isChargebeePaymentIntentAuthorizedError,
} from '@/utils/chargebeePaymentIntent';
import { formatTotalPrice } from '@/utils/pricing';
import { isEmailValid } from '@/utils/email';
import { shouldHideEmail, shouldShowLoader } from '@/utils/conditionalRendering';
import { getLocale } from '@/utils/get-locale-util';
import { logError } from '@/utils/logger';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { useLatestRef } from '@/hooks/useLatestRef';
import { useTranslate } from '@/hooks/useTranslate';
import { Email } from '../Common/Email';
import { Loader } from '../Common/Loader';
import { PaymentProcessOverlay } from '../Common/PaymentProcessOverlay';
import { ProcessingLoaderOverlay } from '../ProcessingLoader/ProcessingLoader';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { SubscriptionDisclaimer } from '../Subscriptions/SubscriptionDisclaimer/SubscriptionDisclaimer';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';

type ComponentState = 'loading' | 'ready' | 'unavailable';

/**
 * Chargebee Payment Component for the long tail of subscription APMs (Kakao
 * Pay, Naver Pay, KR card, …) — `GenericPayForm`'s CHARGEBEE branch, the
 * sibling of {@link StripeGenericPayForm}. One drop-in per box, restricted to
 * exactly this method, wrapped in the same Email / ZIP frame every other APM
 * form uses. Every SDK call goes through {@link chargebeeService}; the finalize
 * runs through `runPayment` → `dispatchByProvider`'s CHARGEBEE case.
 *
 * The CTA is Chargebee's `PaymentButtonComponent`, NOT our PayButton: redirect
 * methods (Kakao Pay, Naver Pay, …) open their auth window from inside the
 * component's `js.chargebee.com` iframe, and a click on our button never
 * grants that frame user activation — `window.open` returns null and Chargebee
 * fails with "Cannot read properties of null (reading 'location')". A click on
 * their same-origin button does. Our fields still gate it via `onButtonClick`;
 * the authorized intent arrives through `onAuthorized` → `runPayment`.
 */
export const ChargebeeGenericPayForm = ({ paymentMethod }: { paymentMethod: PaymentMethodEnum }) => {
  const context = useContext(CheckoutContext);
  const {
    customer,
    addressDetails,
    featureFlags,
    template,
    pricing,
    subscription,
    paymentMethodsSettings,
    payment,
    successScreen,
    actions: { setIsFormValid, setIsInPaymentProcess },
  } = context;
  const { t } = useTranslate();
  const [state, setState] = useState<ComponentState>('loading');
  // Whether the drop-in has drawn any input of its own. A redirect method
  // (Kakao Pay, Naver Pay) has none, and its iframe would otherwise sit as a
  // blank block between the email and the button.
  const [hasFields, setHasFields] = useState(false);
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  // Between Chargebee's button click and `onSuccess`/`onError`: the shopper is
  // in the auth window and nothing of ours is running yet.
  const [isAwaitingAuthorization, setIsAwaitingAuthorization] = useState(false);
  const componentRef = useRef<ChargebeeMountedPaymentComponent | null>(null);
  const initializedRef = useRef(false);
  // The mount effect runs once; `context` and `t` change identity every render.
  const contextRef = useLatestRef(context);
  const tRef = useLatestRef(t);

  const cbInstance = subscription?.cbInstance;
  // The component takes `{ id }` and resolves the intent itself, so the thin
  // summary intent is enough. Do NOT wait for `isHydratedChargebeePaymentIntent`:
  // that gate needs `gateway`, which Chargebee omits from `retrieve` for an
  // intent whose gateway is picked at authorization time (every subscription
  // intent today) — the form would spin forever.
  const paymentIntentId = subscription?.paymentIntent?.id;
  const isPaymentIntentUnavailable = subscription?.isPaymentIntentUnavailable ?? false;
  // Backend contract (same as the Stripe branch): `pspPaymentMethodName` carries
  // Chargebee's wire id when it differs from our canonical name.
  const settings = paymentMethodsSettings.find((pm) => pm.paymentMethodName === paymentMethod);
  const paymentMethodType = settings?.pspPaymentMethodName ?? paymentMethod;
  // Shopper data the gateway needs — Kakao Pay (NICEPAY) refuses to authorize
  // without an email. Keyed so a re-sync fires only when a VALID value changes,
  // not on every keystroke (each `update()` remounts the drop-in).
  const shopperContext = useMemo(
    () => buildShopperContext(customer?.email, customer?.geoLocator?.countryCode2, addressDetails?.zipCode),
    [customer?.email, customer?.geoLocator?.countryCode2, addressDetails?.zipCode]
  );
  const shopperContextKey = JSON.stringify(shopperContext);
  const shopperContextRef = useLatestRef({ key: shopperContextKey, value: shopperContext });
  const syncedContextKeyRef = useRef<string>();
  const syncedIntentRef = useRef<{ id: string; amount?: number }>();
  // ROSCA: the recurring CTA must say so — same label PayButton renders.
  const payButtonText = `${t('subscribeAndPay')} ${formatTotalPrice(pricing)}`;
  const payButtonTextRef = useLatestRef(payButtonText);
  const syncedPayButtonTextRef = useRef<string>();
  const isZipCodeValidRef = useLatestRef(isZipCodeValid);
  // Message of the last `onButtonClick` rejection; Chargebee echoes it back
  // through `onError`, which must not read as a component failure.
  const gateRejectionRef = useRef<string | null>(null);
  const isOwnGateRejection = (error: unknown): boolean => {
    const rejection = gateRejectionRef.current;
    if (!rejection) return false;
    gateRejectionRef.current = null;
    return readChargebeeErrorText(error).includes(rejection);
  };

  useEffect(() => {
    // Hydration is out of retries — report unavailable instead of spinning forever.
    if (isPaymentIntentUnavailable && !initializedRef.current) {
      setState('unavailable');
    }
  }, [isPaymentIntentUnavailable]);

  // Hands the component whatever shopper data the debounce hasn't delivered
  // yet, and waits for the remount it triggers — so a paste-then-Pay never
  // reaches `confirm()` without the email.
  const flushShopperContext = async (): Promise<void> => {
    const component = componentRef.current;
    const { key, value } = shopperContextRef.current;
    if (!component || syncedContextKeyRef.current === key) return;
    syncedContextKeyRef.current = key;
    await component.updateContext(value);
  };

  useEffect(() => {
    if (!cbInstance || !paymentIntentId || isPaymentIntentUnavailable || initializedRef.current) return;
    initializedRef.current = true;
    let disposed = false;
    const abort = new AbortController();

    const init = async () => {
      try {
        const { customer: shopper, addressDetails: address } = contextRef.current;
        const mountContext = buildShopperContext(shopper?.email, shopper?.geoLocator?.countryCode2, address?.zipCode);
        syncedContextKeyRef.current = JSON.stringify(mountContext);
        const intentAtMount = contextRef.current.subscription?.paymentIntent;
        syncedIntentRef.current = { id: paymentIntentId, amount: intentAtMount?.amount };
        syncedPayButtonTextRef.current = payButtonTextRef.current;
        const component = await chargebeeService.mountPaymentComponent({
          selector: `#${CHARGEBEE_PAYMENT_COMPONENT_ID}`,
          paymentIntent: { id: paymentIntentId },
          paymentMethodType,
          locale: getLocale().pspLocale,
          context: mountContext,
          button: { selector: `#${CHARGEBEE_PAYMENT_BUTTON_ID}`, text: syncedPayButtonTextRef.current },
          // Our fields gate Chargebee's button; rejecting stops its `confirm()`.
          // Chargebee prints the rejection's message inline under the button
          // and echoes it to `onError`, so throw shopper-facing copy and let
          // `onFailed` recognize it (see `gateRejectionRef`).
          onButtonClick: async () => {
            const { customer: shopper, payment, actions } = contextRef.current;
            const reject = (message: string): never => {
              gateRejectionRef.current = message;
              throw new Error(message);
            };
            if (payment.isInPaymentProcess) reject(tRef.current('processing'));
            if (!isEmailValid(shopper?.email)) reject(tRef.current('fieldErrors.invalidEmail'));
            if (!isZipCodeValidRef.current) reject(tRef.current('fieldErrors.invalidZipCode'));
            trackAndNotifyPaymentClick(contextRef.current, { paymentMethodName: paymentMethod });
            // Chargebee is about to open the auth window; freeze the checkout
            // behind the processing overlay until it reports back.
            actions.setError(null);
            setIsAwaitingAuthorization(true);
            actions.setIsInPaymentProcess(true);
            await flushShopperContext();
          },
          onAuthorized: (intent: ChargebeePaymentIntent) => {
            setIsAwaitingAuthorization(false);
            if (disposed) return;
            // `runPayment` keeps `isInPaymentProcess` up through finalize + polling.
            void contextRef.current.actions.runPayment({ paymentIntent: intent });
          },
          onFailed: (error) => {
            // Our own gate said no (e.g. a click while a payment is already
            // running) — nothing of Chargebee's failed, and the in-flight
            // attempt's flags must stay up.
            if (isOwnGateRejection(error)) return;
            setIsAwaitingAuthorization(false);
            if (disposed) return;
            const { subscription: sub, actions } = contextRef.current;
            // Single-use intent an earlier attempt authorized (its finalize
            // failed, or Chargebee reports it authorized): re-confirming can
            // never succeed, so finalize with it — the wallets' recovery.
            const authorizedIntent = sub?.paymentIntent;
            if (
              authorizedIntent &&
              (isChargebeePaymentIntentAuthorized(authorizedIntent) || isChargebeePaymentIntentAuthorizedError(error))
            ) {
              void actions.runPayment({ paymentIntent: authorizedIntent });
              return;
            }
            actions.setIsInPaymentProcess(false);
            if (isChargebeeUserClosedError(error)) return;
            logError('Chargebee payment component failed', error);
            actions.setError(tRef.current('somethingWentWrong'));
          },
          onFieldsRendered: () => {
            if (!disposed) setHasFields(true);
          },
          signal: abort.signal,
        });
        if (disposed) {
          component.close();
          return;
        }
        componentRef.current = component;
        setState('ready');
      } catch (error) {
        if (disposed) return;
        logError('Chargebee payment component unavailable', error, { paymentMethodType });
        setState('unavailable');
      }
    };

    void init();
    return () => {
      disposed = true;
      abort.abort();
      componentRef.current?.close();
      componentRef.current = null;
      initializedRef.current = false;
    };
    // `isPaymentIntentUnavailable` is read for the initial guard only: a
    // hydration retrieve that fails AFTER the drop-in is up (ZIP recalc
    // re-read) must not tear a working method down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cbInstance, paymentIntentId, paymentMethodType]);

  // A ZIP tax recalc updates the intent's amount SERVER-side under the same id,
  // so `useChargebeePaymentIntent` hands down a NEW object — tell the component
  // to re-read it (v2's `update({ paymentIntent: { id } })`). Only when the
  // amount actually moved: `update()` remounts the drop-in, and doing that
  // with the intent the shopper just AUTHORIZED makes Chargebee re-read a
  // single-use intent and fail with "Intent status is invalid" while our
  // finalize is in flight.
  const paymentIntent = subscription?.paymentIntent;
  useEffect(() => {
    const component = componentRef.current;
    if (!paymentIntent || !component || isChargebeePaymentIntentAuthorized(paymentIntent)) return;
    const synced = syncedIntentRef.current;
    if (synced?.id === paymentIntent.id && synced.amount === paymentIntent.amount) return;
    syncedIntentRef.current = { id: paymentIntent.id, amount: paymentIntent.amount };
    component.updatePaymentIntent(paymentIntent);
  }, [paymentIntent, state]);

  // Email typed (or ZIP changed) after mount: hand the new values to the
  // component, debounced so a shopper finishing "a@b.co" → "a@b.com" doesn't
  // remount it twice.
  useEffect(() => {
    if (!componentRef.current || syncedContextKeyRef.current === shopperContextKey) return;
    const timer = setTimeout(() => {
      // `flushShopperContext` (a Pay click) may have delivered it meanwhile.
      if (!componentRef.current || syncedContextKeyRef.current === shopperContextKey) return;
      syncedContextKeyRef.current = shopperContextKey;
      void componentRef.current.updateContext(shopperContext);
    }, SHOPPER_CONTEXT_SYNC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // `shopperContext` is derived from the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopperContextKey, state]);

  // A ZIP tax recalc changes the total; keep Chargebee's button label in step.
  useEffect(() => {
    if (componentRef.current && syncedPayButtonTextRef.current !== payButtonText) {
      syncedPayButtonTextRef.current = payButtonText;
      componentRef.current.updateButtonText(payButtonText);
    }
  }, [payButtonText, state]);

  const isReady = state === 'ready';
  const isFormValid = isReady && isZipCodeValid && isEmailValid(customer?.email);
  useEffect(() => {
    setIsFormValid(isFormValid);
  }, [isFormValid, setIsFormValid]);

  const onTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  // Chargebee may never call back when the shopper just closes the auth
  // window; let them unfreeze the checkout themselves, like <RedirectPayment/>.
  const cancelAuthorization = useCallback(() => {
    setIsAwaitingAuthorization(false);
    setIsInPaymentProcess(false);
  }, [setIsInPaymentProcess]);

  // Chargebee's button has no in-flight state of ours, and <ProcessingLoader/>
  // is keyed to specific methods — so this form owns its processing UI: the
  // "complete payment in the window" card (cancellable) while the shopper is
  // in Chargebee's auth window, then the bare spinner through finalize +
  // subscription polling, until the success screen. Both portal to `body`:
  // the Modal's `transform` would otherwise trap a `fixed` overlay inside it.
  const showProcessingOverlay = payment.isInPaymentProcess && !successScreen?.isOpen && shouldShowLoader(featureFlags);

  if (state === 'unavailable') {
    return <PaymentMethodNotSupported />;
  }

  return (
    <div className="flex flex-col gap-4">
      {showProcessingOverlay &&
        (isAwaitingAuthorization ? (
          <PaymentProcessOverlay onCancel={cancelAuthorization} />
        ) : (
          createPortal(<ProcessingLoaderOverlay />, document.body)
        ))}
      {!shouldHideEmail(featureFlags, customer, template.features) && <Email />}
      <InputTaxComponent onValidate={onTaxValidationChange} />
      {/* One spinner stands in for the whole Chargebee area until the service
          reports the drop-in drawn (`onAppReady` + `onPaymentMethodChange`) and
          its button mounted — then everything appears at once, instead of the
          iframe, its fields and the button dropping in one after another. */}
      {!isReady && (
        <div data-testid="chargebee-payment-component-loader" className="flex h-28 w-full items-center justify-center">
          <Loader size="xs" variant="checkout" />
        </div>
      )}
      {/* Chargebee's hosts stay mounted while loading (not display:none) so its
          iframes size against the real width and the readiness callbacks fire;
          zero-height + hidden keeps them out of sight and out of the layout. */}
      <div
        data-testid="chargebee-payment-area"
        className={isReady ? 'flex flex-col gap-4' : 'invisible h-0 overflow-hidden'}
      >
        {/* Collapsed (never unmounted — `confirm()` runs inside it) while the
            drop-in has no fields to show. Chargebee pins an inline height on
            its host, so the wrapper is what collapses. */}
        <div data-testid="chargebee-payment-component-frame" className={hasFields ? 'w-full' : 'h-0 overflow-hidden'}>
          <div id={CHARGEBEE_PAYMENT_COMPONENT_ID} data-testid="chargebee-payment-component" className="w-full" />
        </div>
        {/* ROSCA/FTC renewal disclosure directly above the CTA (renders null
            outside subscriptions). */}
        <SubscriptionDisclaimer />
        {/* Chargebee's Pay button, not ours: redirect methods open their auth
            window from the component's iframe, and only a click inside a
            Chargebee frame grants it the user activation `window.open` needs.
            Chargebee owns this node's children (it clears them on mount). */}
        <div id={CHARGEBEE_PAYMENT_BUTTON_ID} data-testid="chargebee-payment-button" className="w-full" />
      </div>
    </div>
  );
};

const SHOPPER_CONTEXT_SYNC_DEBOUNCE_MS = 400;

/** Only a VALID email is forwarded — a half-typed one would be rejected by the gateway anyway. */
const buildShopperContext = (
  email: string | undefined,
  countryCode: string | undefined,
  zip: string | undefined
): ChargebeePaymentComponentContext => ({
  ...(isEmailValid(email) ? { customer: { email } } : {}),
  ...(countryCode || zip
    ? { billingAddress: { ...(countryCode ? { countryCode } : {}), ...(zip ? { zip } : {}) } }
    : {}),
});
